'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { loadAsset } from '../../helpers/asset-helper';
import { cn } from '../../helpers/cn';
import { getCssVar, isThemeLight as isLightAgainst } from '../../helpers/color-helper';
import { IconDownload16 } from '../../icons/ic-download';
import { IconExternalLink16 } from '../../icons/ic-externalLink';
import Modal, { ModalProps } from '../../modals/Modal';

const ageRatingInfoImageDarkTheme = loadAsset('/images/im-ageRatingInfo-darkTheme.png');
const ageRatingInfoImageLightTheme = loadAsset('/images/im-ageRatingInfo-lightTheme.png');

/** Intrinsic size of both explainer assets — the declared ratio has to match, or the table distorts. */
const AGE_RATING_INFO_IMAGE_WIDTH = 1920;
const AGE_RATING_INFO_IMAGE_HEIGHT = 1200;

/** Parental-approval form ("Muttizettel") — a German-law document, as FSK ratings themselves are. */
const PARENTAL_APPROVAL_PDF_URL = 'https://cdn.cineamo.com/tmp/muttizettel.pdf';
const FSK_WEBSITE_URL = 'https://fsk.de';

type FskInfoModalProps = ModalProps & {
    /**
     * Forces the light or dark explainer artwork. Left unset, it follows the modal panel's own
     * colours — which every app already themes through the `--non-transparent-surface-color`
     * variables, so a cinema website gets artwork suiting its palette without passing its domain
     * configuration in.
     */
    isThemeLight?: boolean;
};

/**
 * Whether the modal panel reads as light, from the resolved theme variables. Reading the computed
 * values is the only way to learn the theme: they are set per app — and per cinema domain — on an
 * ancestor element. Safe to call while rendering: this modal is only ever mounted by a user opening
 * it, so there is no server pass for the client value to diverge from.
 */
function hasLightPanel(): boolean {
    if (typeof document === 'undefined') {
        return false;
    }
    const surfaceColor = getCssVar(document.documentElement, '--non-transparent-surface-color');
    const onSurfaceColor = getCssVar(document.documentElement, '--on-non-transparent-surface-color');
    if (!surfaceColor || !onSurfaceColor) {
        return false;
    }
    return isLightAgainst(surfaceColor, onSurfaceColor);
}

export default function FskInfoModal({ isThemeLight, ...modalProps }: FskInfoModalProps): React.JSX.Element {
    const t = useTranslations();
    const isLightTheme = isThemeLight ?? hasLightPanel();

    return (
        <Modal
            {...modalProps}
            // The panel is `w-full` by default, which on a desktop viewport stretches the explainer
            // to a size where it no longer fits vertically. Capping the width keeps the whole modal
            // on screen while leaving the rating table large enough to read (the asset is 1920px wide).
            className={cn('max-w-[1200px]', modalProps.className)}>
            {/* `h-auto` + the real 1920×1200 ratio so the table is never cropped or squashed, and a
                viewport cap so the image yields space to the links instead of pushing them into a
                scroll. `min-h-0` lets it shrink inside the modal's scrollable flex body. */}
            <img
                className="w-full h-auto min-h-0 max-h-[55vh] lg:max-h-[70vh] object-contain"
                width={AGE_RATING_INFO_IMAGE_WIDTH}
                height={AGE_RATING_INFO_IMAGE_HEIGHT}
                src={isLightTheme ? ageRatingInfoImageLightTheme : ageRatingInfoImageDarkTheme}
                alt={t('movie.fsk-info')}
            />
            {/* Stacked rows on mobile, side by side from `lg`. `flex-none` keeps the links out of
                the shrinking the image absorbs.
                Each link is a centred text block with its icon inline rather than a flex row: a
                label long enough to wrap (the German one does, on a phone) would otherwise shrink
                as a flex item to the full remaining width and leave the icon stranded at the far
                left. Inline, the icon stays attached to the first word however the text wraps. */}
            <div className="flex flex-none flex-col lg:flex-row lg:justify-center gap-4 lg:gap-32 pt-16 text-body-bold-14 text-[var(--primary-dark-color)]">
                <a
                    className="block min-h-[44px] px-8 py-12 text-center hover:opacity-80 transition-opacity"
                    href={PARENTAL_APPROVAL_PDF_URL}
                    download
                    target="_blank"
                    rel="noreferrer">
                    <IconDownload16
                        className="inline-block align-middle mr-8"
                        fill="currentColor"
                    />
                    <span className="align-middle">{t('movie.fsk-parental-approval-download')}</span>
                </a>
                <a
                    className="block min-h-[44px] px-8 py-12 text-center hover:opacity-80 transition-opacity"
                    href={FSK_WEBSITE_URL}
                    target="_blank"
                    rel="noreferrer">
                    <IconExternalLink16
                        className="inline-block align-middle mr-8"
                        fill="currentColor"
                    />
                    <span className="align-middle">FSK.de</span>
                </a>
            </div>
        </Modal>
    );
}
